import React, { useContext, useState, useEffect, useMemo } from "react";
import { Card, Alert } from "react-bootstrap";
import { AudioStatusIcon } from "../Applicant";
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext } from "../Context";
import { languageContent, safeParse } from "../Util";
import { QuestionProps } from "./Props";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { ApplicationStateContext } from "../Contexts/ApplicationStateContext";
import { Alert as AkAlert } from "@aidkitorg/component-library";
import { GoBackToSectionButton } from "../Util";
import { useSurveySections } from "../Contexts/SurveySectionsContext";

export function Validated(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const { applicationStateChanged } = useContext(ApplicationStateContext);
  const { sections } = useSurveySections();

  const metadata: { navigateBackToSection?: string } = useMemo(() => safeParse(props.Metadata!, {}), [props.Metadata]);

  const section = useMemo(() =>
    metadata.navigateBackToSection ?
      sections.sections?.find(s => {
        const sectionMeta: { sectionId?: string } = typeof s.Metadata === 'string' 
          ? safeParse(s.Metadata || "{}")
          : s.Metadata;

        return sectionMeta.sectionId === metadata.navigateBackToSection
      })
      : undefined,
  [metadata, sections]);

  const [result, setResult] = useState(props.info[props["Target Field"]!]);
  const [error, setError] = useState<string>(props.info[props["Target Field"]! + "_error"] || "");

  const advisory = (props["Additional Options"] || []).indexOf('Advisory') !== -1;

  const alertOption = (props['Additional Options'] || [])
    .find(option => option.startsWith('Alert:') && !option.includes('dismissible') && !option.includes('icon:'));
  const alertStyle = alertOption ? alertOption.replace('Alert:', '').trim() : null;

  const alertIconOption = (props['Additional Options'] || [])
    .find(option => option.startsWith('Alert:icon:'));
  const customIconName = alertIconOption ? alertIconOption.replace('Alert:icon:', '').trim() : null;

  const alertConfig = !alertStyle ? null : {
    variant: alertStyle,
    dismissible: (props['Additional Options'] || []).includes('Alert:dismissible'),
    iconName: customIconName
  };

  const isAlert = !!alertConfig;

  useEffect(() => {
    if (Object.keys(config).length === 0) return;

    if (result !== props.info[props["Target Field"]!]) {
      setResult(props.info[props["Target Field"]!]);
    }
    if (error !== props.info[props["Target Field"]! + "_error"]) {
      setError(props.info[props["Target Field"]! + "_error"]);
    }
  }, [result, error, props, props.info, config]);

  const errorContent = (props[languageContent(context.lang)] || '');

  const errorElement = useModularMarkdown({
    content: errorContent,
    info: props.info,
  });

  useEffect(() => {
    const validityState = typeof error === 'undefined' || error === null ? 'unentered' : (!error || advisory || isAlert) ? 'valid' : 'invalid';

    applicationStateChanged({
      questionId: props.Question,
      type: 'change',
      update: {
        validity: {
          state: validityState,
          message: errorContent
        }
      }
    });
  }, [error, errorContent, advisory, isAlert, props.Question, applicationStateChanged]);

  const cardHeader = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
  });

  const alertContentElement = useMemo(() => {
    return error && typeof error === 'string' && error.indexOf('$content') !== -1
      ? errorElement
      : (error ? <span>{error}</span> : (result ? <span>{result}</span> : null));
  }, [error, errorElement, result]);

  if ((props.info[props['Target Field']!] && props.info[props['Target Field']!].indexOf('hide') !== -1) ||
    (props["Additional Options"] || []).indexOf("Hidden") !== -1) {
    return <></>
  }

  const voiceoverDisabled = !!publicConfig.interface?.disableVoiceover;

  const optional = (props["Additional Options"] || []).indexOf('Optional') !== -1;
  if ((props["Additional Options"] || []).indexOf("Hidden Unless Invalid") !== -1) {
    if (!error) {
      return <></>;
    }
  }

  const bg = error ? (advisory ? 'primary' : 'danger') : 'light';

  const mapToAlertVariant = (variant: string): "warning" | "success" | "primary" | "secondary" | "danger" => {
    switch (variant) {
      case 'info':
        return 'primary';
      case 'error':
        return 'danger';
      default:
        return variant as "warning" | "success" | "primary" | "secondary" | "danger";
    }
  };

  const getAlertIcon = (variant: string) => {
    switch (variant) {
      case 'error':
        return 'XCircleIcon';
      case 'warning':
        return 'ExclamationTriangleIcon';
      case 'success':
        return 'CheckCircleIcon';
      case 'info':
        return 'InformationCircleIcon';
      case 'default':
        return 'InformationCircleIcon';
      default:
        return 'InformationCircleIcon';
    }
  };

  const getAlertClassName = (variant: string) => {
    switch (variant) {
      case 'error':
        return 'border-l-4 border-l-red-500 bg-red-50 border-t-0 border-r-0 border-b-0';
      case 'warning':
        return 'border-l-4 border-l-yellow-500 bg-yellow-50 border-t-0 border-r-0 border-b-0';
      case 'success':
        return 'border-l-4 border-l-green-500 bg-green-50 border-t-0 border-r-0 border-b-0';
      case 'info':
        return 'border-l-4 border-l-blue-500 bg-blue-50 border-t-0 border-r-0 border-b-0';
      case 'default':
        return 'border-l-4 border-l-gray-500 bg-gray-50 border-t-0 border-r-0 border-b-0';
      default:
        return 'border-l-4 border-l-gray-500 bg-gray-50 border-t-0 border-r-0 border-b-0';
    }
  };

  const alertVariant = isAlert ? alertConfig.variant : 'default';
  const alertIcon = isAlert && alertConfig.iconName ? alertConfig.iconName as any : getAlertIcon(alertVariant);
  const alertClassName = getAlertClassName(alertVariant);
  const mappedAlertVariant = mapToAlertVariant(alertVariant);

  return (
    <div className={"survey-question relative pb-8 text-clip"}
      data-name={props['Question']}>
      {props.lastQuestion !== true && <span className="absolute top-10 left-8 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />}
      <div className="relative flex space-x-4">
        <div className={voiceoverDisabled ? 'pt-1.5' : '-pt-1'}>
          <span className={(voiceoverDisabled ? 'h-6 w-6 ' : '-ml-2 h-10 w-10 ') + 'rounded-full flex items-center justify-center ring-4 ring-white bg-white '}>
            {props.info[props["Target Field"]!] ?
              <AudioStatusIcon questionId={props.id} state="complete" info={props.info} />
              : (optional || advisory || isAlert ?
                <AudioStatusIcon questionId={props.id} state="optional" info={props.info} /> :
                <AudioStatusIcon questionId={props.id} state='required' info={props.info} />
              )}
          </span>
        </div>
        <div className={'min-w-0 flex-1 pt-1.5 flex justify-between space-x-4'}>
          <div className="w-full">
            {isAlert ? (
              // Show as Alert component when Alert style is specified
              <AkAlert variant={mappedAlertVariant}
                dismissible={alertConfig?.dismissible || false}
                iconName={alertIcon}
                className={alertClassName}
              >
                {alertContentElement}
              </AkAlert>
            ) : (
              // Original styling when no Alert style specified
              error && typeof error === 'string' && error.indexOf('$content') !== -1 ? (
                <div className={`${advisory ? 'bg-blue-100' : 'bg-red-100'} border-md p-3 rounded-md text-gray-800`}>
                  <span>
                    {errorElement}
                  </span>
                  {metadata.navigateBackToSection && section ? <GoBackToSectionButton sectionId={section.id} /> : null}
                </div>
              ) : (
                <Card>
                  <Card.Header as="h6">
                    {cardHeader}
                  </Card.Header>
                  <Card.Body as="div" className={`alert alert-${bg} mb-0`}>
                    <Card.Text className="text-gray-600">
                      {error && <span>{error}</span>}
                      {result && <span>{result}</span>}
                    </Card.Text>
                  </Card.Body>
                </Card>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
